"use client";

import { IconGlobe } from "@decibeltrade/react-icons";
import { insetFocusRing } from "@decibeltrade/web-core/recipes";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { css, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { WatchlistItem } from "@/app/(main)/trade/watchlist/watchlist-item";
import { openLanguageSwitcherModal } from "@/components/modal/open-language-switcher-modal";
import { HIDDEN_MARKET_ADDRS } from "@/constants/feature-flags";
import { calculateSignalStrength, useAppHealthState } from "@/contexts/health";
import { useMarketContexts } from "@/contexts/queries/market-context";
import { useHiddenPerpMarketNames } from "@/contexts/queries/markets";
import { spotChangePct24h, useSpotAssetContexts } from "@/contexts/queries/spot-asset-contexts";
import { useHydrated } from "@/hooks/use-hydrated";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import { addressComparisonKey } from "@/utils/address";

import { Clickable } from "./clickable";
import { EncryptionIndicator } from "./encryption-indicator";
import { DefaultError, HandleSuspenseQuery } from "./handle-query";
import { MediaQuery } from "./media-query";
import { Skeleton } from "./skeleton";
import { ConnectionIndicator } from "./vectors/connection-indicator";

export function Footer({ background }: { background?: string }) {
  const t = useTranslations("footer");

  const isFullNodeDown = useAppHealthState((state) => state.isFullNodeDown);
  const isMissingMarkets = useAppHealthState((state) => state.isMissingMarkets);
  const latency = useAppHealthState((state) => state.latency);
  const isPriceFeedStale = useAppHealthState((state) => state.isPriceFeedStale);
  const signalStrength = calculateSignalStrength({ latency, isFullNodeDown, isPriceFeedStale });

  return (
    <MediaQuery display="above" breakpoint="desktop">
      {(className) => (
        <div className={cx(footerStyles, className)} style={{ background }}>
          {!isMissingMarkets ? <Watchlist /> : <div />}
          <div className={flex({ alignItems: "center", gap: "12", flexShrink: "0" })}>
            <EncryptionIndicator />
            <div className={flex({ alignItems: "center", gap: "4" })}>
              <p className={css({ textStyle: "body.md", color: "text.secondary", mb: "-4" })}>
                {t("connection")}
              </p>
              <ConnectionIndicator signal={signalStrength} />
            </div>
            <Clickable
              type="button"
              ariaLabel={t("changeLanguage")}
              data-testid="language-button"
              onClick={openLanguageSwitcherModal}
              className={cx(languageGlobeButtonStyles, insetFocusRing())}
            >
              <IconGlobe className={css({ w: "20", h: "20" })} />
            </Clickable>
          </div>
        </div>
      )}
    </MediaQuery>
  );
}

function Watchlist() {
  // Watched markets live in localStorage, and a market query started here on the server makes the
  // page's HydrationBoundary defer hydrating it, so the page's server HTML misses its prefetch.
  const hydrated = useHydrated();
  if (!hydrated) return <WatchlistLoadingSkeleton />;

  return (
    <HandleSuspenseQuery
      loading={<WatchlistLoadingSkeleton />}
      error={(fallbackProps) => <DefaultError {...fallbackProps} direction="horizontal" />}
    >
      <WatchlistItems />
    </HandleSuspenseQuery>
  );
}

function WatchlistItems() {
  const { data: marketContexts } = useMarketContexts();
  const { data: spotContexts } = useSpotAssetContexts();
  const { watchedMarkets } = useUserPreferences();
  const hiddenPerpNames = useHiddenPerpMarketNames();

  const visibleMarkets = useMemo(() => {
    // A market watched before it was hidden stays hidden here too.
    const perp = marketContexts.filter(
      (mc) => watchedMarkets.includes(mc.market) && !hiddenPerpNames.has(mc.market),
    );
    // Watched spot markets, shaped like the perp context rows the item renders.
    const spot = Object.values(spotContexts)
      .filter(
        (sc) =>
          watchedMarkets.includes(sc.name) &&
          !HIDDEN_MARKET_ADDRS.has(addressComparisonKey(sc.market_addr)),
      )
      .map((sc) => ({
        market: sc.name,
        volume_24h: sc.volume_24h_quote,
        open_interest: 0,
        previous_day_price: sc.prev_day_price ?? 0,
        price_change_pct_24h: spotChangePct24h(sc) ?? 0,
      }));
    return [...perp, ...spot];
  }, [marketContexts, spotContexts, watchedMarkets, hiddenPerpNames]);

  return (
    <div className={watchlistStyles}>
      {visibleMarkets.map((mc) => (
        <WatchlistItem key={mc.market} marketContext={mc} />
      ))}
    </div>
  );
}

function WatchlistLoadingSkeleton() {
  return (
    <div className={watchlistStyles}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Skeleton key={index} className={css({ width: "88", height: "16", rounded: "200" })} />
      ))}
    </div>
  );
}

const footerStyles = flex({
  minH: "40",
  px: "16",
  alignItems: "center",
  w: "[100vw]",
  position: "fixed",
  bottom: "0",
  justifyContent: "space-between",
  bg: "background.canvas",
  zIndex: "[10]",
});

const watchlistStyles = flex({
  alignItems: "center",
  gap: "28",
  overflowX: "auto",
  mr: "16",
  scrollbar: "hidden",
});

const languageGlobeButtonStyles = css({
  p: "8",
  m: "-8",
  color: "text.secondary",
  _hover: { color: "text.primary" },
  transitionPreset: "colors",
});
